import { useState, useMemo } from 'react';
import { colors } from './styles';
import { BIBLE_BOOKS } from '../data/bibleBooks';

// Lookup map: bookId -> name
const BOOK_NAME = Object.fromEntries(BIBLE_BOOKS.map(b => [b.id, b.name]));

function Badge({ type, children }) {
  const styles = {
    lang:   { background: colors.infoBg,   color: colors.infoText  },
    lesson: { background: colors.purpleBg, color: colors.purpleText },
    tag:    { background: colors.greenBg,  color: colors.greenText  },
    ch:     { background: colors.amberBg,  color: colors.amberText  },
  };
  return (
    <span style={{
      display: 'inline-block', fontSize: 11, padding: '3px 10px',
      borderRadius: 20, fontWeight: 600, letterSpacing: '0.2px',
      ...styles[type],
    }}>
      {children}
    </span>
  );
}

function FilterSelect({ value, onChange, options, placeholder, icon }) {
  const [focused, setFocused] = useState(false);
  const isActive = !!value;
  return (
    <div style={{ position: 'relative', minWidth: 155 }}>
      <span style={{
        position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)',
        fontSize: 13, pointerEvents: 'none', zIndex: 1,
        color: isActive ? colors.primary : colors.textLight,
      }}>{icon}</span>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          width: '100%',
          padding: '10px 30px 10px 30px',
          border: `1px solid ${focused ? colors.borderFocus : isActive ? colors.primary : colors.border}`,
          borderRadius: 10,
          background: isActive ? 'rgba(91,110,232,0.1)' : colors.bgCard,
          color: isActive ? colors.navActiveText : colors.textMuted,
          fontSize: 13, fontFamily: 'inherit', outline: 'none',
          cursor: 'pointer', appearance: 'none',
          boxSizing: 'border-box', transition: 'all 0.2s ease',
          boxShadow: focused ? '0 0 0 3px rgba(91,110,232,0.15)' : 'none',
          fontWeight: isActive ? 600 : 400,
        }}
      >
        <option value="" style={{ background: '#1C2333', color: '#8892B0' }}>{placeholder}</option>
        {options.map(opt => (
          <option key={opt.value} value={opt.value} style={{ background: '#1C2333', color: '#E8EAF6' }}>
            {opt.label}
          </option>
        ))}
      </select>
      <span style={{
        position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
        fontSize: 9, pointerEvents: 'none',
        color: isActive ? colors.primary : colors.textLight,
      }}>▼</span>
    </div>
  );
}

function VideoCard({ video, onEdit, onDelete }) {
  const date     = video.publishedAt
    ? new Date(video.publishedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    : '';
  const hues    = [210, 270, 140, 30, 340, 180, 60];
  const hue     = hues[(video.bookId || 0) % hues.length];
  const bookName = BOOK_NAME[video.bookId] || BOOK_NAME[Number(video.bookId)];

  return (
    <div className="card-hover" style={{
      background: colors.bgCard, border: `1px solid ${colors.border}`,
      borderRadius: 14, padding: '18px 20px',
      transition: 'all 0.2s ease', position: 'relative', overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', left: 0, top: 0, bottom: 0, width: 3,
        background: `linear-gradient(180deg, hsl(${hue},70%,60%), hsl(${hue},70%,40%))`,
        borderRadius: '14px 0 0 14px',
      }} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, paddingLeft: 12 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ marginBottom: 6 }}>
            <div style={{ 
                    fontSize: 15, fontWeight: 700, color: colors.text, marginBottom: 4, letterSpacing: '-0.2px',
                    textDecoration: video.is_deleted ? 'line-through' : 'none',  // ← add this
                    opacity: video.is_deleted ? 0.6 : 1,                         // ← optional: dim it too
                  }}>
                    {video.title}
            </div>
            <div style={{ fontSize: 12, color: colors.textMuted, display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
              {video.author && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ opacity: 0.5 }}>👤</span> {video.author}
                </span>
              )}
              {(bookName || video.bookId) && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ opacity: 0.5 }}>📖</span> {bookName || `Book ${video.bookId}`}
                </span>
              )}
              {date && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ opacity: 0.5 }}>📅</span> {date}
                </span>
              )}
            </div>
          </div>

          {video.videoLink && (
            <a href={video.videoLink} target="_blank" rel="noreferrer" style={{
              fontSize: 12, color: colors.primary,
              display: 'inline-flex', alignItems: 'center', gap: 5,
              marginBottom: 8, wordBreak: 'break-all', textDecoration: 'none',
            }}>
              <span>▶</span>
              <span style={{ opacity: 0.8, maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {video.videoLink}
              </span>
            </a>
          )}

          {video.description && (
            <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 10, lineHeight: 1.6, maxWidth: 600 }}>
              {video.description}
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {video.language && <Badge type="lang">{video.language}</Badge>}
            {video.lesson   && <Badge type="lesson">{video.lesson}</Badge>}
            {(video.tags || '').split(',').filter(Boolean).map((t, i) => <Badge key={i} type="tag">{t.trim()}</Badge>)}
            {(video.chapters || '').split(',').filter(Boolean).map((c, i) => <Badge key={i} type="ch">Ch {c.trim()}</Badge>)}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
  <button className="btn-hover-ghost" onClick={() => onEdit(video)} style={{
    padding: '7px 14px', fontSize: 12,
    border: `1px solid ${colors.border}`, borderRadius: 8,
    background: 'transparent', cursor: 'pointer',
    color: colors.textMuted, fontWeight: 500,
    transition: 'all 0.15s ease', display: 'flex', alignItems: 'center', gap: 5,
  }}>✎ Edit</button>

  {video.is_deleted ? (
    <button className="btn-hover-ghost" onClick={() => onDelete(video, false)} style={{
      padding: '7px 14px', fontSize: 12,
      border: `1px solid rgba(52,211,153,0.4)`, borderRadius: 8,
      background: 'rgba(52,211,153,0.1)', cursor: 'pointer',
      color: '#34D399', fontWeight: 500,
      transition: 'all 0.15s ease', display: 'flex', alignItems: 'center', gap: 5,
    }}>↩ Restore</button>
  ) : (
    <button className="btn-hover-danger" onClick={() => onDelete(video, true)} style={{
      padding: '7px 14px', fontSize: 12,
      border: `1px solid ${colors.dangerBorder}`, borderRadius: 8,
      background: colors.dangerBg, cursor: 'pointer',
      color: colors.danger, fontWeight: 500,
      transition: 'all 0.15s ease', display: 'flex', alignItems: 'center', gap: 5,
    }}>✕ Delete</button>
  )}
</div>
      </div>
    </div>
  );
}

export default function VideoList({ videos, search, onSearchChange, onRefresh, onCreate, onEdit, onDelete }) {
  const [langFilter, setLangFilter]   = useState('');
  const [bookFilter, setBookFilter]   = useState('');
  const [lessonFilter, setLessonFilter] = useState('');
  const [chapterFilter, setChapterFilter] = useState('');

  // Derive unique languages from actual video data
  const languageOptions = useMemo(() => {
    const langs = [...new Set(
      videos.map(v => v.language).filter(Boolean)
    )].sort();
    return langs.map(l => ({ value: l, label: l }));
  }, [videos]);

  // Derive unique books from actual video data (only books that appear in videos)
  const bookOptions = useMemo(() => {
  const bookIds = [...new Set(
    videos
      .filter(v => !langFilter || v.language === langFilter)
      .map(v => Number(v.bookId))
      .filter(Boolean)
  )].sort((a, b) => a - b);
  return bookIds.map(id => ({
    value: String(id),
    label: BOOK_NAME[id] ? `${BOOK_NAME[id]}` : `Book ${id}`,
  }));
}, [videos, langFilter]);

  const lessonOptions = useMemo(() => {
  const lessons = [...new Set(
    videos
      .filter(v =>
        (!langFilter || v.language === langFilter) &&
        (!bookFilter || String(v.bookId) === bookFilter)
      )
      .map(v => v.lesson)
      .filter(Boolean)
  )].sort();
  return lessons.map(l => ({ value: l, label: l }));
}, [videos, langFilter, bookFilter]);

const chapterOptions = useMemo(() => {
  const chapters = [...new Set(
    videos
      .filter(v =>
        (!langFilter || v.language === langFilter) &&
        (!bookFilter || String(v.bookId) === bookFilter)
      )
      .flatMap(v =>
        (v.chapters || '').split(',').map(c => c.trim()).filter(Boolean)
      )
  )].sort((a, b) => Number(a) - Number(b));
  return chapters.map(c => ({ value: c, label: `Chapter ${c}` }));
}, [videos, langFilter, bookFilter]);


  const activeFilterCount = [langFilter, bookFilter, lessonFilter, chapterFilter].filter(Boolean).length;

  const filtered = useMemo(() => videos.filter(v => {
    const q = search.toLowerCase();
    const matchesSearch = !search || (
      (v.title    || '').toLowerCase().includes(q) ||
      (v.author   || '').toLowerCase().includes(q) ||
      (v.language || '').toLowerCase().includes(q) ||
      (v.lesson   || '').toLowerCase().includes(q)
    );
    const matchesLang = !langFilter || v.language === langFilter;
    const matchesBook = !bookFilter || String(v.bookId) === bookFilter;
    const matchesLesson  = !lessonFilter  || v.lesson === lessonFilter;
    const matchesChapter = !chapterFilter || (v.chapters || '').split(',').map(c => c.trim()).includes(chapterFilter);
    return matchesSearch && matchesLang && matchesBook && matchesLesson && matchesChapter;
  }), [videos, search, langFilter, bookFilter, lessonFilter, chapterFilter]);

  const hasActiveFilters = search || langFilter || bookFilter || lessonFilter || chapterFilter;

  const clearAll = () => {
    onSearchChange('');
    setLangFilter('');
    setBookFilter('');
    setLessonFilter('');
    setChapterFilter('');
  };

  return (
    <>
      {/* ── Search row ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 220, position: 'relative' }}>
          <span style={{
            position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)',
            color: colors.textLight, fontSize: 15, pointerEvents: 'none',
          }}>🔍</span>
          <input
            value={search}
            onChange={e => onSearchChange(e.target.value)}
            placeholder="Search by title, author, language, lesson..."
            style={{
              width: '100%', padding: '10px 14px 10px 38px',
              border: `1px solid ${colors.border}`, borderRadius: 10,
              background: colors.bgCard, fontSize: 14, color: colors.text,
              fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
              transition: 'all 0.2s ease',
            }}
          />
        </div>

        <button className="btn-hover-ghost" onClick={onRefresh} style={{
          padding: '10px 16px', border: `1px solid ${colors.border}`,
          background: colors.bgCard, color: colors.textMuted, borderRadius: 10,
          fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
          transition: 'all 0.15s ease',
        }}>↻ Refresh</button>
      </div>

      {/* ── Filter row ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        marginBottom: '1.25rem', flexWrap: 'wrap',
      }}>
         <FilterSelect
          value={langFilter}
          onChange={v => { setLangFilter(v); setBookFilter(''); setLessonFilter(''); setChapterFilter(''); }}
          options={languageOptions}
          placeholder="All Languages"
          icon="🌐"
        />

{langFilter && (
  <FilterSelect
    value={bookFilter}
    onChange={v => { setBookFilter(v); setLessonFilter(''); setChapterFilter(''); }}
    options={bookOptions}
    placeholder="All Books"
    icon="📖"
  />
)}

{(langFilter && bookFilter) && (
  <>
    <FilterSelect
      value={lessonFilter}
      onChange={setLessonFilter}
      options={lessonOptions}
      placeholder="All Lessons"
      icon="📚"
    />

    <FilterSelect
      value={chapterFilter}
      onChange={setChapterFilter}
      options={chapterOptions}
      placeholder="All Chapters"
      icon="📄"
    />
  </>
)}

        {/* Active filter chips */}
        {langFilter && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: colors.infoBg, color: colors.infoText,
            border: `1px solid rgba(96,165,250,0.3)`,
            borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 600,
          }}>
            🌐 {langFilter}
            <button onClick={() => setLangFilter('')} style={{
              background: 'none', border: 'none', color: colors.infoText,
              cursor: 'pointer', fontSize: 13, padding: 0, lineHeight: 1,
              opacity: 0.7,
            }}>✕</button>
          </span>
        )}

        {bookFilter && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: colors.amberBg, color: colors.amberText,
            border: `1px solid rgba(251,191,36,0.3)`,
            borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 600,
          }}>
            📖 {BOOK_NAME[Number(bookFilter)] || `Book ${bookFilter}`}
            <button onClick={() => setBookFilter('')} style={{
              background: 'none', border: 'none', color: colors.amberText,
              cursor: 'pointer', fontSize: 13, padding: 0, lineHeight: 1,
              opacity: 0.7,
            }}>✕</button>
          </span>
        )}

        {/* Clear all */}
        {activeFilterCount > 1 && (
          <button onClick={clearAll} style={{
            background: 'none', border: 'none', color: colors.textLight,
            cursor: 'pointer', fontSize: 12, padding: '4px 8px',
            textDecoration: 'underline', transition: 'color 0.15s',
          }}
            onMouseEnter={e => e.currentTarget.style.color = colors.danger}
            onMouseLeave={e => e.currentTarget.style.color = colors.textLight}
          >
            Clear all
          </button>
        )}

        {/* Result count on the right */}
        <div style={{ marginLeft: 'auto', fontSize: 12, color: colors.textLight, whiteSpace: 'nowrap' }}>
          {hasActiveFilters
            ? `${filtered.length} of ${videos.length} videos`
            : `${videos.length} video${videos.length !== 1 ? 's' : ''}`}
        </div>
      </div>

      {/* ── Cards ── */}
      {filtered.length === 0 ? (
        <div style={{
          textAlign: 'center', color: colors.textMuted, fontSize: 14,
          padding: '5rem 1rem', background: colors.bgCard, borderRadius: 14,
          border: `1px solid ${colors.border}`,
        }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
          <div style={{ fontWeight: 600, marginBottom: 6, color: colors.text }}>
            {videos.length === 0 ? 'No videos yet' : 'No results found'}
          </div>
          <div style={{ fontSize: 13, color: colors.textLight }}>
            {videos.length === 0
              ? 'Click "+ Add Video" in the header to create your first entry.'
              : 'Try adjusting your search or filters.'}
          </div>
          {hasActiveFilters && videos.length > 0 && (
            <button onClick={clearAll} style={{
              marginTop: 14, padding: '8px 18px',
              background: 'rgba(91,110,232,0.15)', color: colors.navActiveText,
              border: `1px solid rgba(91,110,232,0.3)`, borderRadius: 8,
              cursor: 'pointer', fontSize: 13, fontWeight: 600,
            }}>Clear filters</button>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {filtered.map(v => (
            <VideoCard key={v.id} video={v} onEdit={onEdit} onDelete={onDelete} />
          ))}
        </div>
      )}
    </>
  );
}
